<template>
  <div
    v-if="showNavigator"
    class="venue-navigator"
  >
    <div class="d-flex justify-center align-center pa-4">
      <!-- Browse context label (hidden for now restore flex-column wrapper + p below if re-enabled)
      <p
        class="venue-navigator__context
               text-caption text-primary text-center font-weight-medium
               rounded-pill mb-2 pointer-events-none"
      >
        {{ browseContextCopy }}
      </p>
      <div class="d-flex justify-center align-center">
      -->
        <v-btn
          class="font-weight-regular mr-4"
          rounded
          @click="navigateToPrevious"
        >
          <template #prepend>
            <v-icon size="small">mdi-chevron-left</v-icon>
          </template>
          Previous
        </v-btn>

        <v-btn
          class="font-weight-regular"
          rounded
          @click="navigateToNext"
        >
          <template #append>
            <v-icon size="small">mdi-chevron-right</v-icon>
          </template>
          Next
        </v-btn>
      <!-- </div> -->
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const VENUE_LIST_MODAL_STORAGE_PREFIX = 'bw_vl_modal_';
const VENUE_LIST_MODAL_QUERY = 'vlModal';

export default {
  name: 'VenueNavigator',
  computed: {
    ...mapGetters('venues', { findVenuesByDealId: 'findByDealId' }),
    dealId() {
      return parseInt(this.$route.params.dealId, 10);
    },
    venueId() {
      return parseInt(this.$route.params.venueId, 10);
    },
    allDealVenues() {
      if (!Number.isFinite(this.dealId)) return [];
      return this.findVenuesByDealId(this.dealId);
    },
    venueListModalPayload() {
      if (!Number.isFinite(this.dealId)) return null;
      try {
        const raw = sessionStorage.getItem(
          `${VENUE_LIST_MODAL_STORAGE_PREFIX}${this.dealId}`
        );
        if (!raw) return null;
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    filteredNavigationIds() {
      const ids = this.venueListModalPayload?.navigationVenueIds;
      if (!Array.isArray(ids) || ids.length < 1) return null;
      const inDeal = new Set(this.allDealVenues.map(v => v.id));
      const filtered = ids
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && inDeal.has(id));
      return filtered.length ? filtered : null;
    },
    hasFilteredBrowseQuery() {
      const q = this.$route.query || {};
      return q[VENUE_LIST_MODAL_QUERY] === '1' || q[VENUE_LIST_MODAL_QUERY] === 'true';
    },
    /** Filter modal flow with exactly one matching place (no prev/next). */
    hasSingleFilteredResultContext() {
      if (!this.hasFilteredBrowseQuery || !this.filteredNavigationIds) {
        return false;
      }
      if (this.filteredNavigationIds.length !== 1) return false;
      return this.filteredNavigationIds[0] === this.venueId;
    },
    /** Two or more filtered places. prev/next stay within this list. */
    isFilteredBrowseContext() {
      if (!this.hasFilteredBrowseQuery || !this.filteredNavigationIds) {
        return false;
      }
      if (this.filteredNavigationIds.length < 2) return false;
      return this.filteredNavigationIds.includes(this.venueId);
    },
    venuesForNavigation() {
      if (this.isFilteredBrowseContext && this.filteredNavigationIds) {
        return this.filteredNavigationIds.map(id => ({ id }));
      }
      if (this.hasSingleFilteredResultContext) {
        return [{ id: this.venueId }];
      }
      return this.allDealVenues;
    },
    currentVenueIndex() {
      return this.venuesForNavigation.findIndex(({ id }) => id === this.venueId);
    },
    showNavigator() {
      return this.venuesForNavigation?.length > 1;
    },
    nextVenueId() {
      const list = this.venuesForNavigation;
      const nextVenueIndex = this.currentVenueIndex + 1;
      const venueIndex = nextVenueIndex < list.length ? nextVenueIndex : 0;

      return list[venueIndex]?.id;
    },
    previousVenueId() {
      const list = this.venuesForNavigation;
      const previousVenueIndex = this.currentVenueIndex - 1;
      const venueIndex =
        previousVenueIndex >= 0 ? previousVenueIndex : list.length - 1;

      return list[venueIndex]?.id;
    }
    /*
    browseContextCopy() {
      const list = this.venuesForNavigation;
      if (!list?.length || list.length < 2) return '';
      const idx = list.findIndex(v => v.id === this.venueId);
      const pos = idx >= 0 ? idx + 1 : 0;
      const n = list.length;
      if (this.isFilteredBrowseContext) {
        return `Browsing filtered results (${pos} of ${n})`;
      }
      return `Browsing all places on this pass (${pos} of ${n})`;
    }
    */
  },
  mounted() {
    this.setCurrentVenueId(this.venueId);
  },
  methods: {
    ...mapActions('venues', ['navigateToVenue', 'setCurrentVenueId']),
    navigateToPrevious() {
      if (!this.previousVenueId) return;
      const query = { ...this.$route.query };
      this.navigateToVenue({
        venueId: this.previousVenueId,
        dealId: this.dealId,
        token: query.t,
        query
      });
    },
    navigateToNext() {
      if (!this.nextVenueId) return;
      const query = { ...this.$route.query };
      this.navigateToVenue({
        venueId: this.nextVenueId,
        dealId: this.dealId,
        token: query.t,
        query
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.venue-navigator {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding-bottom: 90px;
  pointer-events: none;

  /*
  .venue-navigator__context {
    max-width: 100%;
    background: rgba(var(--v-theme-primary), 0.1);
    padding: 2px 16px;
  }
  */

  .v-btn {
    --v-theme-overlay-multiplier: 0;
    --v-theme-primary-overlay-multiplier: 0;
    background: white !important;
    border-color: rgba(0, 0, 0, 0.23) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    color: rgba(0, 0, 0, 0.87) !important;
    pointer-events: auto;
  }
}
</style>